import { useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { AnimatePresence, LayoutGroup, MotionConfig, motion, useDragControls } from "motion/react";
import {
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { AuthFrame, AuthLayout } from "../features/auth/AuthShell";
import { TwoFactorSetup } from "../features/auth/TwoFactorSetup";
import { useFirstSteps } from "../features/settings/FirstSteps";
import { welcomeSkipKey } from "../features/settings/welcome";
import { api } from "../lib/api";
import { authClient } from "../lib/auth";
import { ROLE_LABEL } from "../lib/format";
import { LiveProvider, useLive } from "../lib/live";
import { NotificationsProvider } from "../lib/notifications";
import { useLumiConfig, useMe, useWaitingConversations } from "../lib/queries";
import { currentTheme, onThemeChange, setTheme } from "../lib/theme";
import {
  BackIcon,
  BuildingIcon,
  CalendarIcon,
  CheckIcon,
  ContactsIcon,
  HistoryUserIcon,
  InboxIcon,
  KnowledgeIcon,
  MenuIcon,
  MoonIcon,
  ProductsIcon,
  QuoteIcon,
  ReportIcon,
  SettingsIcon,
  SparkleIcon,
  SunIcon,
  UserIcon,
  WrenchIcon,
} from "./icons";
import { NotificationBell } from "./NotificationBell";
import { Empty } from "./ui";

/** Sidebar + work area. Also handles users with no company, or several, before showing the panel. */
export function AppShell() {
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { nav, ...navHandlers } = useMagnification();
  const [collapsed, setCollapsed] = useCollapsed();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const role = useRole();
  const manages = role === "owner" || role === "admin";
  const lumi = useLumiConfig(manages);
  const welcomed = useRef(false);

  // The first time an owner or admin comes in, the welcome tour opens by itself, unless
  // they chose "Lo hago después" in this browser.
  useEffect(() => {
    const setupPending = lumi.data && !lumi.data.setupCompletedAt;
    if (welcomed.current || !manages || !setupPending || path === "/bienvenida") return;
    welcomed.current = true;
    const tenantId = activeTenantOf(me.data)?.tenantId;
    try {
      if (tenantId && localStorage.getItem(welcomeSkipKey(tenantId))) return;
    } catch {
      // Storage blocked: show the tour.
    }
    navigate({ to: "/bienvenida", replace: true });
  }, [lumi.data, manages, path, me.data, navigate]);

  async function logout() {
    await authClient.signOut();
    qc.clear();
    navigate({ to: "/login" });
  }

  if (me.isPending) return null;
  if (me.isError) {
    return (
      <div className="auth-main">
        <Empty title="No pudimos cargar tu cuenta.">
          <button className="btn" type="button" onClick={() => me.refetch()}>
            Reintentar
          </button>
        </Empty>
      </div>
    );
  }

  const { user, tenants, activeTenantId } = me.data;
  const active = tenants.find((t) => t.tenantId === activeTenantId) ?? (tenants.length === 1 ? tenants[0] : undefined);

  if (!active) {
    const brand = {
      side: "left" as const,
      title: tenants.length === 0 ? "Ya casi" : "Varias empresas",
      text:
        tenants.length === 0
          ? "Tu cuenta existe. Falta que te agreguen a tu empresa para ver su WhatsApp."
          : "Tu cuenta tiene acceso a más de una empresa. Puedes cambiar después desde el panel.",
    };
    return (
      <AuthFrame brand={brand}>
        {tenants.length === 0 ? (
          <AuthLayout
            title="Tu cuenta está lista"
            subtitle={`Falta que un administrador de Orbis te agregue a tu empresa. Avísale el correo con el que te registraste: ${user.email}.`}
          >
            <button className="auth-link" type="button" onClick={logout}>
              Cerrar sesión
            </button>
          </AuthLayout>
        ) : (
          <AuthLayout title="Elige la empresa" subtitle="¿En cuál vas a trabajar ahora?">
            <div className="auth-choices">
              {tenants.map((t) => (
                <button
                  key={t.tenantId}
                  type="button"
                  className="auth-secondary"
                  onClick={async () => {
                    await api.put("/me/active-tenant", { tenantId: t.tenantId });
                    qc.invalidateQueries();
                  }}
                >
                  <span>{t.tenantName}</span>
                  <small>{ROLE_LABEL[t.role]}</small>
                </button>
              ))}
            </div>
            <button className="auth-link" type="button" onClick={logout}>
              Cerrar sesión
            </button>
          </AuthLayout>
        )}
      </AuthFrame>
    );
  }

  // Everyone turns on 2FA before using the panel (the API enforces it too).
  if (me.data.requireTwoFactor && !user.twoFactorEnabled) {
    return <TwoFactorSetup onCancel={logout} />;
  }

  // The welcome tour is full screen: no menu behind it.
  if (path === "/bienvenida") {
    return (
      <NotificationsProvider>
        <LiveProvider>
          <MotionConfig reducedMotion="user">
            <Outlet />
          </MotionConfig>
        </LiveProvider>
      </NotificationsProvider>
    );
  }

  const foot = (
    <div className="sidebar-foot">
      <LiveBadge />
      <span>{active.tenantName}</span>
      <Link to="/cuenta" className="who" activeProps={{ className: "active" }} title={`Mi cuenta · ${user.email}`}>
        <UserIcon />
        <span>{user.name}</span>
      </Link>
      <span>{ROLE_LABEL[active.role]}</span>
      <div className="sidebar-foot-row">
        <button type="button" onClick={logout}>
          Cerrar sesión
        </button>
        <span className="sidebar-foot-tools">
          <NotificationBell />
          <ThemeToggle />
        </span>
      </div>
    </div>
  );

  return (
    <NotificationsProvider>
      <LiveProvider>
        <MotionConfig reducedMotion="user">
          <div className={`shell${collapsed ? " collapsed" : ""}`}>
            <aside className="sidebar">
              <div className="sidebar-top">
                <img className="sidebar-logo" src="/brand/orbis-logo-negativo.svg" alt="Orbis" />
                <img className="sidebar-symbol" src="/brand/orbis-simbolo-negativo.svg" alt="Orbis" />
                <button
                  type="button"
                  className="sidebar-toggle"
                  aria-label={collapsed ? "Expandir menú" : "Encoger menú"}
                  aria-expanded={!collapsed}
                  title={collapsed ? "Expandir menú" : "Encoger menú"}
                  onClick={() => setCollapsed(!collapsed)}
                >
                  <BackIcon />
                </button>
              </div>
              <LayoutGroup id="sidebar">
                <MainNav navRef={nav} handlers={navHandlers} collapsed={collapsed} />
              </LayoutGroup>
              {foot}
            </aside>
            <main className="main">
              <Outlet />
            </main>
            <PhoneMenu foot={foot} />
          </div>
        </MotionConfig>
      </LiveProvider>
    </NotificationsProvider>
  );
}

interface NavItem {
  to: string;
  label: string;
  Icon: () => ReactNode;
  /** Only owners and admins see it. */
  manage?: boolean;
}

/**
 * Two groups: the daily work, and the setup that owners and admins keep (agents do not
 * see it).
 */
const NAV_GROUPS = [
  {
    label: null,
    manage: false,
    items: [
      { to: "/bandeja", label: "Bandeja", Icon: InboxIcon },
      { to: "/contactos", label: "Contactos", Icon: ContactsIcon },
      { to: "/agenda", label: "Agenda", Icon: CalendarIcon },
      { to: "/postventa", label: "Postventa", Icon: WrenchIcon },
      { to: "/cotizaciones", label: "Cotizaciones", Icon: QuoteIcon },
      { to: "/reportes", label: "Reportes", Icon: ReportIcon },
      { to: "/auditoria", label: "Auditoría", Icon: HistoryUserIcon, manage: true },
    ],
  },
  {
    label: "Configuración",
    manage: true,
    items: [
      { to: "/lumi", label: "Lumi", Icon: SparkleIcon },
      { to: "/catalogo", label: "Catálogo", Icon: ProductsIcon },
      { to: "/conocimiento", label: "Conocimiento", Icon: KnowledgeIcon },
      { to: "/empresa", label: "Empresa", Icon: BuildingIcon },
      { to: "/ajustes", label: "Ajustes", Icon: SettingsIcon },
    ],
  },
] as const;

/**
 * The menu. The current page keeps its own cyan pill; a second, glassy one slides to the
 * item under the mouse (or with focus) and back under the current page when the pointer
 * leaves, so the eye follows one moving shape and never loses where it is.
 */
function MainNav({
  navRef,
  handlers,
  collapsed,
  onPick,
}: {
  navRef?: RefObject<HTMLElement | null>;
  handlers?: Omit<ReturnType<typeof useMagnification>, "nav">;
  collapsed: boolean;
  /** Called when an item is chosen (the phone menu closes). */
  onPick?: () => void;
}) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const role = useRole();
  const [hovered, setHovered] = useState<string | null>(null);
  const canManage = role === "owner" || role === "admin";
  const groups = NAV_GROUPS.filter((g) => !g.manage || canManage).map((g) => ({
    ...g,
    items: (g.items as readonly NavItem[]).filter((i) => !i.manage || canManage),
  }));
  const items: readonly NavItem[] = groups.flatMap((g): readonly NavItem[] => g.items);
  const current = items.find((i) => path === i.to || path.startsWith(`${i.to}/`))?.to ?? null;
  const pill = hovered ?? current;
  const waiting = useWaitingConversations("all").data?.items.length ?? 0;

  return (
    <nav
      className="nav"
      aria-label="Principal"
      ref={navRef}
      {...handlers}
      onMouseLeave={() => setHovered(null)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setHovered(null);
      }}
    >
      {(role === "owner" || role === "admin") && <FirstStepsLink collapsed={collapsed} onPick={onPick} />}
      {groups.map((group) => [
        group.label && (
          <span key={group.label} className="nav-group" aria-hidden="true">
            {group.label}
          </span>
        ),
        ...group.items.map(({ to, label, Icon }) => (
          <Link
            key={to}
            to={to}
            activeProps={{ className: "active" }}
            title={collapsed ? label : undefined}
            style={{ "--i": items.findIndex((i) => i.to === to) } as CSSProperties}
            onClick={onPick}
            onPointerEnter={(e) => {
              if (e.pointerType === "mouse") setHovered(to);
            }}
            onFocus={() => setHovered(to)}
          >
            {pill === to && (
              <motion.span
                layoutId="nav-pill"
                className="nav-pill"
                aria-hidden="true"
                transition={{ type: "spring", stiffness: 520, damping: 42, mass: 0.8 }}
              />
            )}
            {current === to && <span className="nav-current" aria-hidden="true" />}
            <Icon />
            <span className="nav-label">{label}</span>
            {to === "/bandeja" && <WaitingCount count={waiting} />}
          </Link>
        )),
      ])}
    </nav>
  );
}

/** Spring for the button turning into the menu and back. */
const MORPH = { type: "spring", stiffness: 380, damping: 34, mass: 0.9 } as const;

/**
 * Phones: a floating glass button that grows into the whole menu, where the thumb is,
 * and shrinks back into the button when it closes (a pick, a tap outside, Escape or a
 * swipe down). More modules fit here than in a tab bar, and the list can change per
 * role or company.
 */
function PhoneMenu({ foot }: { foot: ReactNode }) {
  const [open, setOpen] = useState(false);
  const sheet = useRef<HTMLDivElement>(null);
  // Only the grip drags the sheet down: a drag on the whole sheet takes vertical touches
  // away from its scroll, and a long menu could not be scrolled on a phone.
  const drag = useDragControls();
  // The button is a new element after each close: focus it once it exists again.
  const refocus = useRef(false);
  // Only a button that comes back from the menu fades its icon in; on page load it is just there.
  const opened = useRef(false);
  const waiting = useWaitingConversations("all").data?.items.length ?? 0;

  useEffect(() => {
    if (!open) return;
    // Focus the current page's item, so the keyboard starts where the eye is.
    (sheet.current?.querySelector<HTMLElement>("a.active") ?? sheet.current?.querySelector<HTMLElement>("a"))?.focus({
      preventScroll: true,
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      refocus.current = true;
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => {
    refocus.current = true;
    setOpen(false);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="backdrop"
            className="phone-menu-backdrop"
            aria-hidden="true"
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
        )}
      </AnimatePresence>
      {open ? (
        <motion.div
          key="sheet"
          ref={sheet}
          layoutId="phone-menu"
          id="phone-menu"
          className="phone-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Menú"
          style={{ borderRadius: 24 }}
          transition={MORPH}
          drag="y"
          dragControls={drag}
          dragListener={false}
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.6 }}
          onDragEnd={(_, info) => {
            if (info.offset.y > 80 || info.velocity.y > 500) close();
          }}
        >
          {/* The content waits for the shape to grow, so it never looks squashed. */}
          <motion.div
            className="phone-menu-body"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0, transition: { delay: 0.12, duration: 0.2 } }}
          >
            <div className="phone-menu-grip" aria-hidden="true" onPointerDown={(e) => drag.start(e)}>
              <span />
            </div>
            <LayoutGroup id="phone">
              <MainNav collapsed={false} onPick={() => setOpen(false)} />
            </LayoutGroup>
            {/* Mi cuenta lives in the footer: choosing it closes the menu too. */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: only bubbles clicks from its link */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: the link inside handles the keyboard */}
            <div onClick={(e) => (e.target as HTMLElement).closest("a") && setOpen(false)}>{foot}</div>
          </motion.div>
        </motion.div>
      ) : (
        <motion.button
          key="button"
          ref={(el) => {
            if (el && refocus.current) {
              refocus.current = false;
              el.focus({ preventScroll: true });
            }
          }}
          layoutId="phone-menu"
          type="button"
          className="menu-fab"
          aria-label="Abrir menú"
          aria-expanded={false}
          aria-controls="phone-menu"
          style={{ borderRadius: 18 }}
          transition={MORPH}
          onClick={() => {
            opened.current = true;
            setOpen(true);
          }}
        >
          <motion.span
            className="menu-fab-icon"
            initial={opened.current ? { opacity: 0, scale: 0.6 } : false}
            animate={{ opacity: 1, scale: 1, transition: { delay: 0.1, duration: 0.18 } }}
          >
            <MenuIcon />
          </motion.span>
          <WaitingCount count={waiting} />
        </motion.button>
      )}
    </>
  );
}

/** Light or dark in one tap; Mi cuenta also offers "like the device". */
function ThemeToggle() {
  const [dark, setDark] = useState(() => currentTheme() === "dark");
  useEffect(() => onThemeChange(() => setDark(currentTheme() === "dark")), []);
  return (
    <button
      type="button"
      className="theme-toggle"
      aria-label={dark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
      title={dark ? "Modo claro" : "Modo oscuro"}
      onClick={() => setTheme(dark ? "light" : "dark")}
    >
      {/* Both icons stay mounted: the current one turns in as the other turns out. */}
      <span className="theme-icon theme-icon-sun" data-shown={!dark}>
        <SunIcon />
      </span>
      <span className="theme-icon theme-icon-moon" data-shown={dark}>
        <MoonIcon />
      </span>
    </button>
  );
}

/** The company the panel is working in (one company: that one). */
function activeTenantOf(me: ReturnType<typeof useMe>["data"]) {
  return me?.tenants.find((t) => t.tenantId === me.activeTenantId) ?? me?.tenants[0];
}

/**
 * "Primeros pasos 3/6" at the top of the menu until everything is done: it goes to the
 * next pending step. Owners and admins only.
 */
function FirstStepsLink({ collapsed, onPick }: { collapsed: boolean; onPick?: () => void }) {
  const state = useFirstSteps(true);
  if (!state?.next) return null;
  const { done, total, next } = state;
  return (
    <Link
      to={next.to}
      search={next.search ?? {}}
      className="first-steps-link"
      title={collapsed ? `Primeros pasos: ${done} de ${total}` : undefined}
      onClick={onPick}
    >
      <CheckIcon />
      <span className="nav-label">Primeros pasos</span>
      <span className="first-steps-count">
        {done}/{total}
      </span>
      <span className="first-steps-bar" aria-hidden="true">
        <i style={{ transform: `scaleX(${done / total})` }} />
      </span>
    </Link>
  );
}

/** Conversations waiting for an answer, on the Bandeja item. */
function WaitingCount({ count }: { count: number }) {
  return (
    <AnimatePresence initial={false}>
      {count > 0 && (
        <motion.span
          key="count"
          className="nav-count"
          title={`${count} esperando respuesta`}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.5, opacity: 0 }}
          transition={{ type: "spring", stiffness: 600, damping: 30 }}
        >
          <span className="sr-only">Esperando respuesta: </span>
          {/* A new number pops a little, so a fresh message catches the eye. */}
          <motion.span key={count} initial={{ scale: 1.35 }} animate={{ scale: 1 }} style={{ display: "inline-block" }}>
            {count > 99 ? "99+" : count}
          </motion.span>
        </motion.span>
      )}
    </AnimatePresence>
  );
}

const COLLAPSED_KEY = "orbis.sidebar.collapsed";

/** Icons-only menu on desktop, remembered per browser. */
function useCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // Storage blocked (private mode): the choice lasts until reload.
    }
  }, [collapsed]);
  return [collapsed, setCollapsed] as const;
}

function LiveBadge() {
  const live = useLive();
  return (
    <span
      className={`live-badge${live ? " on" : ""}`}
      title={live ? "Recibiendo cambios al instante" : "Reconectando…"}
    >
      <i aria-hidden="true" />
      {live ? "En vivo" : "Reconectando…"}
    </span>
  );
}

/**
 * Role of the signed-in user in the active company. Subscribes to the `me` query, so a
 * role change pushed by the live socket re-renders every screen that checks it.
 */
export function useRole() {
  const me = useMe().data;
  if (!me) return null;
  const active = me.tenants.find((t) => t.tenantId === me.activeTenantId) ?? me.tenants[0];
  return active?.role ?? null;
}

/**
 * Dock-style magnification for the desktop menu: icons grow with how close the mouse is.
 * Off for reduced motion and touch screens.
 */
function useMagnification() {
  const nav = useRef<HTMLElement>(null);
  const frame = useRef(0);
  const enabled = () =>
    window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function onPointerMove(e: PointerEvent<HTMLElement>) {
    if (e.pointerType !== "mouse" || !enabled()) return;
    const y = e.clientY;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      for (const link of nav.current?.querySelectorAll<HTMLElement>("a") ?? []) {
        const r = link.getBoundingClientRect();
        const d = y - (r.top + r.height / 2);
        // Gaussian falloff: up to +35% right under the pointer, neighbours a little.
        link.style.setProperty("--mag", (1 + 0.35 * Math.exp(-(d * d) / (2 * 34 * 34))).toFixed(3));
      }
    });
  }

  function onPointerLeave() {
    cancelAnimationFrame(frame.current);
    for (const link of nav.current?.querySelectorAll<HTMLElement>("a") ?? []) link.style.removeProperty("--mag");
  }

  return { nav, onPointerMove, onPointerLeave };
}
